import { useId } from 'react';
import clsx from 'clsx';

export interface ControlOption {
  value: string;
  label: string;
  description?: string;
}

export interface RadioGroupProps {
  name: string;
  label: string;
  options: ControlOption[];
  value: string | undefined;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  /** `stack` (default) renders one option per row; `grid-2` renders a 2-col responsive grid. */
  layout?: 'stack' | 'grid-2';
}

export function RadioGroup({
  name,
  label,
  options,
  value,
  onChange,
  error,
  hint,
  layout = 'stack',
}: RadioGroupProps) {
  const baseId = useId();
  const errorId = error ? `${baseId}-error` : undefined;
  const hintId = hint ? `${baseId}-hint` : undefined;

  return (
    <fieldset
      aria-describedby={clsx(hintId, errorId) || undefined}
      aria-invalid={error ? true : undefined}
    >
      <legend className="block font-mono text-[0.625rem] uppercase tracking-[0.125em] text-ink-subtle mb-2">
        {label}
      </legend>
      <div
        className={clsx(
          'gap-2',
          layout === 'grid-2'
            ? 'grid grid-cols-1 md:grid-cols-2'
            : 'flex flex-col'
        )}
      >
        {options.map((opt) => {
          const id = `${baseId}-${opt.value}`;
          const selected = value === opt.value;
          return (
            <label
              key={opt.value}
              htmlFor={id}
              className={clsx(
                'flex items-start gap-3 rounded-md border px-3 py-2.5 cursor-pointer transition-colors',
                selected
                  ? 'border-blue bg-blue-whisper/40'
                  : 'border-hairline bg-panel hover:border-ink/30'
              )}
            >
              <input
                type="radio"
                id={id}
                name={name}
                value={opt.value}
                checked={selected}
                onChange={() => onChange(opt.value)}
                className="mt-1 h-4 w-4 border-hairline text-blue-deep focus:ring-blue/30"
              />
              <span className="flex flex-col">
                <span className="font-display text-[0.9375rem] text-ink leading-tight">
                  {opt.label}
                </span>
                {opt.description ? (
                  <span className="font-display text-[0.8125rem] text-ink-subtle leading-snug mt-0.5">
                    {opt.description}
                  </span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
      {hint ? (
        <p
          id={hintId}
          className="mt-2 font-display text-[0.8125rem] text-ink-subtle"
        >
          {hint}
        </p>
      ) : null}
      {error ? (
        <p
          id={errorId}
          className="mt-2 font-display text-[0.8125rem] text-[#b33a3a]"
        >
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

export interface CheckboxGroupProps {
  label: string;
  options: ControlOption[];
  values: string[];
  onChange: (values: string[]) => void;
  error?: string;
  hint?: string;
  layout?: 'stack' | 'grid-2';
}

export function CheckboxGroup({
  label,
  options,
  values,
  onChange,
  error,
  hint,
  layout = 'stack',
}: CheckboxGroupProps) {
  const baseId = useId();
  const errorId = error ? `${baseId}-error` : undefined;
  const hintId = hint ? `${baseId}-hint` : undefined;

  function toggle(v: string) {
    if (values.includes(v)) {
      onChange(values.filter((x) => x !== v));
    } else {
      onChange([...values, v]);
    }
  }

  return (
    <fieldset
      aria-describedby={clsx(hintId, errorId) || undefined}
      aria-invalid={error ? true : undefined}
    >
      <legend className="block font-mono text-[0.625rem] uppercase tracking-[0.125em] text-ink-subtle mb-2">
        {label}
      </legend>
      <div
        className={clsx(
          'gap-2',
          layout === 'grid-2'
            ? 'grid grid-cols-1 md:grid-cols-2'
            : 'flex flex-col'
        )}
      >
        {options.map((opt) => {
          const id = `${baseId}-${opt.value}`;
          const selected = values.includes(opt.value);
          return (
            <label
              key={opt.value}
              htmlFor={id}
              className={clsx(
                'flex items-start gap-3 rounded-md border px-3 py-2.5 cursor-pointer transition-colors',
                selected
                  ? 'border-blue bg-blue-whisper/40'
                  : 'border-hairline bg-panel hover:border-ink/30'
              )}
            >
              <input
                type="checkbox"
                id={id}
                value={opt.value}
                checked={selected}
                onChange={() => toggle(opt.value)}
                className="mt-1 h-4 w-4 border-hairline text-blue-deep focus:ring-blue/30 rounded"
              />
              <span className="flex flex-col">
                <span className="font-display text-[0.9375rem] text-ink leading-tight">
                  {opt.label}
                </span>
                {opt.description ? (
                  <span className="font-display text-[0.8125rem] text-ink-subtle leading-snug mt-0.5">
                    {opt.description}
                  </span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
      {hint ? (
        <p
          id={hintId}
          className="mt-2 font-display text-[0.8125rem] text-ink-subtle"
        >
          {hint}
        </p>
      ) : null}
      {error ? (
        <p
          id={errorId}
          className="mt-2 font-display text-[0.8125rem] text-[#b33a3a]"
        >
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
